import { Link } from "react-router-dom";
import { ArrowLeft, Users, Info } from "lucide-react";

// V3 access frame (C01, C21–C27): dark navigation column, white top bar, breadcrumb, form left, photo context right.
export function PhotoAside() {
  return (
    <div className="space-y-3" data-testid="auth-photo-aside">
      <img src="/v3-gemeinsam-erleben.jpg" alt="Vier Menschen, eine Person davon im Rollstuhl, sitzen lachend an einem Cafétisch."
        className="aspect-[510/335] w-full rounded-2xl object-cover" />
      <div className="rounded-2xl border border-[#E3DDF0] bg-[#EEEAF7] p-4">
        <p className="flex items-center gap-2 font-bold text-brand-purple"><Users className="h-5 w-5 text-brand-magenta" aria-hidden="true" />Gemeinsam erleben</p>
        <p className="mt-2 text-sm text-zinc-600">Menschen kennenlernen, Aktivitäten entdecken und selbstbestimmt teilnehmen.</p>
      </div>
    </div>
  );
}

export function AuthNotice({ title, text, tone = "lavender", testId }) {
  const cls = tone === "amber" ? "border-[#F6E3B4] bg-[#FFF6DD]" : "border-[#E3DDF0] bg-[#EEEAF7]";
  return (
    <div data-testid={testId} className={`rounded-xl border px-4 py-3 ${cls}`}>
      <p className={`flex items-center gap-2 text-sm font-bold ${tone === "amber" ? "text-[#8A5A00]" : "text-brand-purple"}`}>
        <Info className="h-4 w-4 shrink-0" aria-hidden="true" />{title}
      </p>
      {text && <p className="mt-1 text-xs leading-relaxed text-zinc-600">{text}</p>}
    </div>
  );
}

export function AuthFrame({ title, subtitle, children, testId, aside = <PhotoAside />, topRight }) {
  return (
    <div className="min-h-screen bg-[#F6F4FB] lg:pl-60">
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col bg-brand-purple px-4 pb-4 pt-5 lg:flex" aria-label="Control OS">
        <img src="/visibilis-wordmark-light.png" alt="Visibilis.life – Menschen mit Behinderungen" className="h-9 w-auto self-start" />
        <p className="mt-6 px-1 text-xs font-bold text-white/90">Control OS</p>
        <div className="mt-auto px-1 text-[11px] leading-relaxed text-white/70"><p>Hilfe bleibt erreichbar</p><p>VISIBILIS.LIFE</p></div>
      </aside>
      <header className="flex h-16 items-center justify-between gap-3 border-b border-[#ECE8F3] bg-white px-4 sm:px-8">
        <span className="hidden font-bold text-brand-purple lg:inline">Control OS</span>
        <img src="/visibilis-wordmark-dark.png" alt="Visibilis.life" className="h-8 w-auto lg:hidden" />
        <div className="flex items-center gap-3 text-sm text-zinc-600">{topRight}<span className="hidden sm:inline" data-testid="auth-personal-access">Persönlicher Zugang</span></div>
      </header>
      <main data-testid={testId} className="px-4 py-6 sm:px-8 lg:py-8">
        <p className="hidden text-xs font-semibold text-zinc-500 lg:block">Übersicht / Plattformverwaltung</p>
        <h1 className="mt-1 break-words text-3xl font-bold leading-tight tracking-tight text-brand-purple hyphens-auto lg:mt-3 lg:text-[2.15rem]" lang="de">{title}</h1>
        {subtitle && <p className="mt-2 text-base text-zinc-600">{subtitle}</p>}
        <div className="mt-6 grid max-w-[1120px] gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-4">{children}</div>
          {aside && <div className="hidden lg:block">{aside}</div>}
        </div>
      </main>
    </div>
  );
}

export function PublicShell({ title, subtitle, children, testId }) {
  return (
    <AuthFrame title={title} subtitle={subtitle} testId={testId}>
      {children}
      <Link to="/login" data-testid="back-to-login"
        className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border border-[#E6E1F0] bg-white px-6 text-sm font-bold text-brand-purple hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zur Anmeldung
      </Link>
    </AuthFrame>
  );
}

export const publicLabel = "mb-1.5 block text-sm font-bold text-brand-purple";
export const publicInput = "h-11 w-full rounded-lg border border-[#E6E1F0] bg-white px-3 text-sm text-brand-purple placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta";
export const publicButton = "flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-magenta px-6 text-sm font-bold text-white hover:bg-brand-magenta-dark disabled:opacity-60 sm:w-auto sm:min-w-[13rem]";
export const publicSecondary = "flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#E6E1F0] bg-white px-6 text-sm font-bold text-brand-purple hover:bg-secondary disabled:opacity-60 sm:w-auto sm:min-w-[8rem]";
